<template>
  <div v-if="isLoggedIn">
    <h4>YOU CANNOT ACCESS THIS PAGE WHILE LOGGED IN. PLEASE LOG OUT AND RE-OPEN THE LINK TO ACCESS.</h4>
  </div>
  <div v-else class="content">
    <div class="reset-wrapper">
      <div class="reset">
        <PublicBrandHeader />
        <h3>Provider Account Setup</h3>
        <p v-if="inviteNotice" class="invite-notice">{{ inviteNotice }}</p>
        <form id="provider-verify-form" @submit.prevent="handleVerifyProvider">
          <div v-if="allFormErrors.length" class="form-error-summary" role="alert">
            <ul>
              <li v-for="(err, idx) in allFormErrors" :key="idx">{{ err }}</li>
            </ul>
          </div>
          <div class="mb-2">
            <div class="row mb-2 gx-2">
              <div class="col-md-6 col-12 form-group form-floating mb-2 mb-lg-0">
                <input type="text" class="form-control" :value="accountTypeLabel" disabled />
                <label class="profile_label">Account Type:</label>
              </div>
              <div class="col-md-6 col-12 form-group form-floating mb-2 mb-lg-0">
                <input type="text" class="form-control" :value="accntnum" disabled />
                <label class="profile_label">Organization Account #:</label>
              </div>
            </div>
            <div class="row mb-2 gx-2">
              <div class="col-md-4 col-12 form-group form-floating mb-2 mb-lg-0">
                <input type="text" class="form-control" v-model="form.first" required />
                <label class="profile_label">First Name:</label>
              </div>
              <div class="col-md-4 col-12 form-group form-floating mb-2 mb-lg-0">
                <input type="text" class="form-control" v-model="form.last" required />
                <label class="profile_label">Last Name:</label>
              </div>
              <div class="col-md-4 col-12 form-group form-floating mb-2 mb-lg-0">
                <input type="text" class="form-control" v-model="form.externid" disabled />
                <label class="profile_label">ID #:</label>
              </div>
            </div>
            <div class="row mb-2 gx-2">
              <div class="col-md-4 col-12 form-group form-floating mb-2 mb-lg-0">
                <input type="text" class="form-control" v-model="form.npi" required />
                <label class="profile_label">NPI:</label>
              </div>
              <div class="col-md-4 col-12 form-group form-floating mb-2 mb-lg-0">
                <input type="email" class="form-control" v-model="form.email" required disabled />
                <label class="profile_label">Account Email:</label>
              </div>
              <div class="col-md-4 col-12 form-group form-floating mb-2 mb-lg-0">
                <select id="provtype" class="form-control" v-model="form.providerType" required>
                  <option value="">Select provider type</option>
                  <option value="Physician">Physicians</option>
                  <option value="Doctors of Osteopathic Medicin">Doctors of Osteopathic Medicine</option>
                  <option value="Physician Assistant">Physician Assistant</option>
                  <option value="CHIROPRACTOR">Chiropractor</option>
                  <option value="PODIATRIST">Podiatrist</option>
                  <option value="DENTAL">Dentist / Dental Hygienist</option>
                  <option value="DENTAL_HYGIENIST">Dental Hygienist</option>
                  <option value="Nurse Practitioner">Nurse Practitioner (NP)</option>
                  <option value="SURGEON">Surgeon</option>
                </select>
                <label for="provtype" class="profile_label">Provider Type:</label>
              </div>
            </div>
            <div class="row mb-2 gx-2">
              <div class="col-md-6 col-12 form-group form-floating mb-2 mb-lg-0">
                <input
                  type="text"
                  class="form-control"
                  v-model="form.login"
                  required
                  placeholder="Required"
                  @keyup="validateUsername"
                />
                <label class="profile_label">Username:</label>
              </div>
              <div class="col-md-3 col-12 form-group form-floating mb-2 mb-lg-0">
                <input type="date" class="form-control" v-model="form.dob" required />
                <label class="profile_label">Date of Birth:</label>
              </div>
              <div class="col-md-3 col-12 form-group form-floating mb-2 mb-lg-0">
                <select class="form-control" v-model="form.gender" required>
                  <option value="">Select gender</option>
                  <option v-for="(gender, index) in genders" :key="'gender-' + index" :value="gender.id">
                    {{ gender.value }}
                  </option>
                </select>
                <label class="profile_label">Gender:</label>
              </div>
            </div>
            <div class="row mb-2 gx-2">
              <div class="col-md-6 col-12 form-group form-floating mb-2 mb-lg-0">
                <input
                  ref="pwd"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-control"
                  v-model="form.password"
                  required
                  placeholder="Enter password"
                  @change="validatePassword"
                />
                <button
                  type="button"
                  class="pwd-toggle"
                  @click="togglePassword"
                  @mousedown.prevent
                  :aria-pressed="showPassword"
                  :title="showPassword ? 'Hide password' : 'Show password'"
                  style="background:none; border:none; position:absolute; top:15px; right:16px; cursor:pointer;"
                >
                  <svg v-if="!showPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" stroke-width="1.5"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.5"/>
                  </svg>
                  <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M3 3l18 18" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M1 12s4-7 11-7c2 0 3 .5 4 1.3" stroke="currentColor" stroke-width="1.5"/>
                  </svg>
                </button>
                <label class="profile_label">Password:</label>
              </div>
              <div class="col-md-6 col-12 form-group form-floating mb-2 mb-lg-0">
                <input
                  ref="pwd2"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-control"
                  v-model="form.password2"
                  required
                  placeholder="Confirm password"
                  @change="confirmPassword"
                />
                <label class="profile_label">Confirm Password:</label>
              </div>
            </div>
            <div class="row mb-2 gx-2">
              <div class="col-md-4 col-12 form-group form-floating mb-2 mb-lg-0">
                <input type="text" class="form-control" v-model="form.cell" placeholder="Required" />
                <label class="profile_label">Phone Number:</label>
              </div>
              <div class="col-md-4 col-12 form-group form-floating mb-2 mb-lg-0">
                <select class="form-select" v-model="form.pref">
                  <option value="Email">Email</option>
                  <option value="SMS">SMS</option>
                  <option value="Phone">Phone</option>
                  <option value="All">All</option>
                </select>
                <label class="profile_label">Communication Preference:</label>
              </div>
            </div>
            <div class="row mb-2 gx-2">
              <div class="col-md-5 col-12 form-group form-floating mb-2 mb-lg-0">
                <input type="text" class="form-control" v-model="form.addr" placeholder="Required" />
                <label class="profile_label">Address:</label>
              </div>
              <div class="col-md-3 col-12 form-group form-floating mb-2 mb-lg-0">
                <input type="text" class="form-control" v-model="form.city" placeholder="Required" />
                <label class="profile_label">City:</label>
              </div>
              <div class="col-md-2 col-12 form-group form-floating mb-2 mb-lg-0">
                <select class="form-select" v-model="form.state">
                  <option value="">Select a state</option>
                  <option v-for="(state, index) in states" :key="index" :value="state.abbr">
                    {{ state.abbr }}
                  </option>
                </select>
                <label class="profile_label">State:</label>
              </div>
              <div class="col-md-2 col-12 form-group form-floating mb-2 mb-lg-0">
                <input type="text" class="form-control" v-model="form.zip" placeholder="Required" />
                <label class="profile_label">Zip:</label>
              </div>
            </div>
            <div class="row mb-2 gx-2">
              <div class="accountform-btn">
                <input type="submit" class="btn btn-primary" value="Complete Registration" id="sbmtproviderbtn" />
                <router-link to="/login" class="btn btn-secondary">Return to log in</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { h } from "vue";
import store from "@/store";
import router from "@/router/index";
import us from "us";
import { CURR_API } from "@/constants/apis";
import { LoginNavigation } from "@/store/modules/fsm/messages";
import VALIDATION_ERRORS from "@/data/validationErrors";
import genders from "@/data/genders";
import { getPasswordPolicyErrors } from "@/utils/passwordPolicy";
import { inviteHeaders } from "@/utils/inviteHeader";
import { notPendingMessage, recheckPendingAccount } from "@/utils/pendingAccountStatus";
import {
  register,
  finalizeRegistration,
  updatePassword,
  updateUsername,
  isUserExist,
  isNpiExist,
} from "@/api/userApi";
import PublicBrandHeader from '@/components/public-layouts/PublicBrandHeader.vue';

export default {
  name: "verify-provider-page",
  // Public registration for providers invited from med orders or pending Add User emails.
  components: {
    PublicBrandHeader,
  },
  props: {
    accountType: { type: String, default: "Provider" },
    unique: { type: String, default: "" },
    accntnum: { type: String, default: "" },
    firstName: { type: String, default: "" },
    lastName: { type: String, default: "" },
    npi: { type: String, default: "" },
    email: { type: String, default: "" },
    patientUniqueId: { type: String, default: "" },
    // The invite e-mail's token: this page has no session, so keyon authorizes
    // creating and linking the provider by it (sent as a header, never a form field).
    invite: { type: String, default: "" },
  },
  data() {
    return {
      accountTypeLabel: "Provider",
      genders,
      states: us.states,
      showPassword: false,
      inviteNotice: "",
      isValidated: true,
      // Provisional username from pending account_user; empty for med-invite create flow.
      originalLogin: "",
      validationAr: [],
      form: {
        externid: "",
        first: "",
        last: "",
        login: "",
        password: "",
        password2: "",
        email: "",
        npi: "",
        providerType: "",
        dob: "1970-01-01",
        gender: "",
        addr: "",
        city: "",
        state: "",
        zip: "",
        cell: "",
        pref: "Email",
        accountType: "Provider",
        admin: false,
        isLoggedIn: false,
        externid2: "",
      },
      formErrors: {},
    };
  },
  computed: {
    isLoggedIn() {
      return store.getters["login/isLoggedIn"];
    },
    isPendingAccountFlow() {
      // Add User flow: account already exists in Keyon as Pending; user sets password only.
      return !!this.unique;
    },
    isMedInviteFlow() {
      // Med-order invite: no pending row yet; org accntnum + npi + email must be in the link.
      return !this.unique && !!this.accntnum && !!this.npi && !!this.email;
    },
    /** Flattens formErrors + validationAr into one top-of-form summary. */
    allFormErrors() {
      const out = [];
      for (const key in this.formErrors) {
        const fieldErrors = this.formErrors[key];
        if (Array.isArray(fieldErrors)) {
          for (const msg of fieldErrors) {
            if (msg) out.push(msg);
          }
        }
      }
      if (Array.isArray(this.validationAr)) {
        for (const msg of this.validationAr) {
          if (msg) out.push(msg);
        }
      }
      return out;
    },
  },
  beforeCreate() {
    // When logged in, template shows "DISABLED" inside private layout — do not alert or redirect.
    const isInit = this.$gotInitialMessage && this.$gotInitialMessage();
    if (!isInit) {
      const href = location.href + "";
      setTimeout(() => location.assign(href), 5000);
    }
  },
  created() {
    for (const key in this.form) {
      this.formErrors[key] = [];
    }
    if (!this.isLoggedIn) {
      this.initializeInvite();
    }
  },
  methods: {
    /** Auto-generated 9-digit provider ID shown read-only on the form. */
    generateUniqueNineId() {
      return Math.floor(Math.random() * 899999999) + 100000000;
    },
    /** Same postgres payload shape as AddUserForm — phone stays in keyon only. */
    buildRegisterPayload() {
      return {
        isLoggedIn: false,
        noValidation: true,
        admin: false,
        externid: this.form.externid,
        externid2: this.form.externid2 || this.accntnum,
        npi: this.form.npi,
        first: this.form.first,
        middle: "",
        last: this.form.last,
        login: this.form.login,
        password: this.form.password,
        password2: this.form.password2,
        email: this.form.email,
        dob: this.form.dob,
        gender: Number(this.form.gender),
        addr: this.form.addr,
        city: this.form.city,
        state: this.form.state,
        zip: this.form.zip,
      };
    },
    /** Flatten postgres register() validation errors for Swal display. */
    formatRegisterError(err) {
      const raw = err?.message || (typeof err === "string" ? err : "");
      if (!raw) {
        return "Unable to complete registration. Please try again.";
      }
      try {
        const parsed = JSON.parse(raw);
        const messages = [];
        Object.keys(parsed).forEach((key) => {
          if (Array.isArray(parsed[key])) {
            parsed[key].forEach((msg) => messages.push(msg));
          }
        });
        if (messages.length) {
          return messages.join("\n");
        }
      } catch (e) {
        // not JSON — use raw message
      }
      return raw;
    },
    /** Route invite type to med-order prefill or pending-account load; reject malformed links. */
    initializeInvite() {
      if (this.isPendingAccountFlow) {
        this.loadPendingAccount();
        return;
      }
      if (this.isMedInviteFlow) {
        this.form.first = this.firstName || "";
        this.form.last = this.lastName || "";
        this.form.email = this.email || "";
        this.form.npi = this.npi || "";
        this.form.externid = this.generateUniqueNineId();
        this.form.externid2 = this.accntnum || "";
        this.inviteNotice =
          "You have been invited to join this organization's EMR as a provider. Please complete the form below to create your account.";
        return;
      }
      router.push({ name: LoginNavigation });
      alert("There is an error with this verification link. Please request a new one.");
    },
    /** Hydrate form from GetPendingAccountInfo for Add User provider invites. */
    loadPendingAccount() {
      this.webHook({
        Account: {
          API_Meth: "GetPendingAccountInfo",
          accountype: this.accountType || "Provider",
          uniqueNine: this.unique,
        },
      }).then((res) => {
        if (res?.data?.status === "No Records Found") {
          router.push({ name: LoginNavigation });
          alert("There is an error with this verification link. Please request a new one.");
          return;
        }
        const dbData = res.data.data && res.data.data[0];
        if (!dbData || dbData.account_status !== "Pending") {
          router.push({ name: LoginNavigation });
          // Expired has its own sentence (utils/pendingAccountStatus.js).
          alert(
            dbData && dbData.account_status !== "Pending"
              ? notPendingMessage(dbData.account_status)
              : "There is an error with this verification link. Please request a new one."
          );
          return;
        }
        this.form.externid = dbData.unique_ID;
        this.form.login = dbData.username;
        this.originalLogin = dbData.username || ""; // baseline for change detection
        this.form.first = dbData.user_Fname;
        this.form.last = dbData.user_Lname;
        this.form.email = dbData.user_email;
        this.form.npi = dbData.proffesionalLicense || dbData.professionalLicense || "";
        this.form.addr = dbData.address || "";
        this.form.city = dbData.city || "";
        this.form.state = dbData.state || "";
        this.form.zip = dbData.zip || "";
        this.form.cell = dbData.user_phone || "";
        this.form.externid2 = dbData.accountnumber || this.accntnum;
        this.inviteNotice =
          "Please review your information and complete your provider account registration.";
      });
    },
    validate() {
      for (const key in this.formErrors) {
        if (this.formErrors[key] && this.formErrors[key].length > 0) {
          return false;
        }
      }
      this.validationAr = [];
      this.isValidated = true;
      if (!this.form.providerType) {
        this.validationAr.push("Provider type is required.");
        this.isValidated = false;
      }
      if (!this.form.gender) {
        this.validationAr.push("Gender is required.");
        this.isValidated = false;
      }
      return this.isValidated;
    },
    // Live username checks (format + uniqueness via existing isUserExist).
    async validateUsername() {
      const usernameRegex = /^([a-zA-Z]|_)+([a-zA-Z0-9_])*$/;
      this.form.login = (this.form.login || "").trim();
      this.formErrors.login = [];
      if (!usernameRegex.test(this.form.login)) {
        this.formErrors.login.push(VALIDATION_ERRORS.USERNAME_CHARACTERS);
      }
      if (this.form.login.length < 8 || this.form.login.length > 24) {
        this.formErrors.login.push(VALIDATION_ERRORS.USERNAME_LENGTH);
      }
      // Skip lookup when invitee keeps the provisional username from Add User.
      if (this.formErrors.login.length < 1 && this.form.login !== this.originalLogin) {
        try {
          const userExists = await isUserExist(this.form.login);
          // Assign (don't push) so overlapping isUserExist replies can't stack the same message.
          this.formErrors.login = userExists ? [VALIDATION_ERRORS.USERNAME_EXISTS] : [];
        } catch (err) {
          this.formErrors.login = [String(err?.message || err || "Unable to check username.")];
        }
      }
    },
    // Write new login to Postgres then MySQL (pending Add User flow only).
    async persistUsernameIfChanged() {
      const next = (this.form.login || "").trim();
      if (!next || next === this.originalLogin) {
        return; // no rename needed
      }
      // Postgres LA_user.ZV_user_login (login authority).
      await updateUsername({
        externid: this.form.externid,
        username: next,
      });
      // MySQL account_user (+ nurses.username when that row already exists).
      const mysqlRes = await this.webHook({
        Account: {
          API_Meth: "UpdatePendingAccountUsername",
          accountnumber: this.accntnum || this.form.externid2 || this.form.accountnumber,
          uniqueNine: this.unique || this.form.externid,
          username: next,
        },
      });
      if (mysqlRes?.data?.status !== "200" && mysqlRes?.data?.message !== "Updated") {
        throw new Error(mysqlRes?.data?.message || "Unable to update username in organization records.");
      }
      this.originalLogin = next; // treat as current for any later checks
    },
    validatePassword() {
      this.formErrors.password = getPasswordPolicyErrors(this.form.password, [
        this.form.first,
        "",
        this.form.last,
      ]);
      if (this.form.password2) {
        this.confirmPassword();
      }
    },
    confirmPassword() {
      this.formErrors.password2 = [];
      if (this.form.password !== this.form.password2) {
        this.formErrors.password2.push(VALIDATION_ERRORS.PASSWORD_MISMATCH);
      }
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
      this.$nextTick(() => {
        const el = this.$refs.pwd;
        if (el && el.focus) el.focus();
      });
    },
    /** Submit handler: pending flow activates account; med invite creates postgres + Keyon records. */
    async handleVerifyProvider() {
      this.validatePassword();
      this.confirmPassword();
      await this.validateUsername(); // await so uniqueness errors block submit
      if (!this.validate()) {
        this.$swal({
          type: "warning",
          title: "Error",
          text: "Please fix the errors in the form to finish registering this account.",
        });
        return;
      }
      const loader = this.$loading.show(
        { container: this.$el },
        { after: () => h("div", {}, "Creating provider account...") }
      );
      try {
        if (this.isPendingAccountFlow) {
          // The invitation may have run out since the page opened (keyon's
          // PendingAccountExpiry): stop before pacmny-be gives them a login.
          const notPending = await recheckPendingAccount(
            (request) => this.webHook(request),
            this.accountType || "Provider",
            this.unique
          );
          if (notPending) {
            loader.hide();
            await this.$swal({ type: "warning", title: "Error", text: notPending });
            router.push({ name: LoginNavigation });
            return;
          }
          await this.persistUsernameIfChanged(); // rename before activate
          await finalizeRegistration(this.form.externid);
          await updatePassword({
            email: this.form.email,
            newPassword: this.form.password,
          });
          await this.VerifyBackEndAccount();
        } else {
          const npiTaken = await isNpiExist(this.form.npi);
          if (npiTaken) {
            throw new Error("This NPI is already registered to another user.");
          }
          if (!this.form.externid) {
            this.form.externid = this.generateUniqueNineId();
          }
          this.form.externid2 = this.accntnum;
          const regData = await register(this.buildRegisterPayload());
          if (!regData?.externid) {
            throw new Error("Registration could not be completed.");
          }
          this.form.externid = regData.externid;
          await this.createKeyonSubAccount();
          await finalizeRegistration(this.form.externid);
        }
        let groupLinkWarning = "";
        if (this.patientUniqueId) {
          try {
            await this.assignProviderGroups();
          } catch (groupErr) {
            groupLinkWarning =
              " Your account was created, but linking to the patient could not be completed. Please contact your administrator.";
            console.error(groupErr);
          }
        }
        loader.hide();
        await this.$swal({
          type: "success",
          title: "Success!",
          text:
            "Your provider account has been created successfully. You will now be redirected to login." +
            groupLinkWarning,
        });
        router.push({ name: LoginNavigation });
      } catch (err) {
        loader.hide();
        this.$swal({
          type: "error",
          title: "Error",
          text: this.formatRegisterError(err),
        });
      }
    },
    /** Create Active Keyon provider sub-account and org Providers group membership. */
    async createKeyonSubAccount() {
      const formdata = new FormData();
      formdata.append("API_Meth", "CreateSubAccount");
      formdata.append("accountnumber", this.accntnum);
      formdata.append("accountType", "Provider");
      formdata.append("accountrole", "Provider");
      formdata.append("uniqueNineID", String(this.form.externid));
      formdata.append("Firstname", this.form.first);
      formdata.append("Lastname", this.form.last);
      formdata.append("Address", this.form.addr || "");
      formdata.append("prCity", this.form.city || "");
      formdata.append("prState", this.form.state || "");
      formdata.append("prZip", this.form.zip || "");
      formdata.append("username", this.form.login);
      formdata.append("password", this.form.password);
      formdata.append("email", this.form.email);
      formdata.append("cell", this.form.cell || "");
      formdata.append("license", this.form.npi);
      formdata.append("providerType", this.form.providerType || "");
      formdata.append("commpref", this.form.pref || "Email");
      formdata.append("dob", this.form.dob || "1970-01-01");
      formdata.append("gender", String(this.form.gender || ""));
      formdata.append("accntStatus", "Active");
      formdata.append("primary_group_name", "Providers");
      const res = await axios.post(CURR_API + "/tswebhook.php", formdata, { headers: inviteHeaders(this.invite) });
      if (res.data?.message !== "Account Created Successfully") {
        throw new Error(res.data?.error || res.data?.message || "Account could not be created.");
      }
    },
    /** Link new provider to the patient who triggered the med-order invite (add-only groups). */
    async assignProviderGroups() {
      const patientId = String(this.patientUniqueId || "").trim();
      const providerId = String(this.form.externid || "").trim();
      const accountnumber = String(this.accntnum || this.form.externid2 || "").trim();
      if (!patientId || !providerId || !accountnumber) {
        return;
      }
      const res = await this.webHook({
        Groups: {
          API_Meth: "AddProviderForPatient_GroupBased",
          accountnumber,
          patient_unique_ID: patientId,
          provider_unique_ID: providerId,
        },
      });
      const status = String(res?.data?.status || "").toLowerCase();
      if (!status.includes("successful")) {
        throw new Error(res?.data?.message || "Provider could not be linked to the patient.");
      }
    },
    /** Activate pending Keyon account after password is set (Add User flow). */
    async VerifyBackEndAccount() {
      return this.webHook({
        Account: {
          API_Meth: "UpdatePendingAccountInfo",
          Accountstatus: "Active",
          uniqueNine: this.isPendingAccountFlow ? this.unique : this.form.externid,
          accountnumber: this.accntnum || this.form.externid2,
        },
      }).then((res) => {
        if (res.data.message === "Updated" && !res.data.error) {
          return "Account Finalized";
        }
        if (res.data.error) {
          throw new Error(res.data.error);
        }
        throw new Error("Account could not be activated.");
      });
    },
    async webHook(request) {
      return axios.post(CURR_API + "/tswebhook.php", request, {
        headers: { "Content-Type": "application/json;", ...inviteHeaders(this.invite) },
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../stylesheets/mixins/alignment";

.content {
  width: 100%;
  height: auto;
  background: url("../../assets/images/bg.jpg") no-repeat center fixed;
  background-size: cover;
  position: relative;
}

.reset-wrapper {
  height: 100vh;
  width: 100%;
  position: relative;
}

.reset {
  background-color: #fff;
  padding: 2rem;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12),
    0 1px 5px 0 rgba(0, 0, 0, 0.2);
  @include absoluteCenter(860px);

  > h3 {
    text-align: center;
  }
}

.invite-notice {
  margin-bottom: 1rem;
  color: #444;
}

.accountform-btn {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.form-error-summary {
  margin: 0.75rem 0 1.5rem;
  padding: 0.9rem 1.15rem;
  background: #fdecea;
  border: 1px solid #f3b4b0;
  border-radius: 6px;
}

.form-error-summary ul {
  margin: 0;
  padding-left: 1.1rem;
  color: #9b2c2c;
}

.form-error-summary li {
  margin: 0.15rem 0;
}

@media only screen and (max-width: 768px) {
  .reset {
    width: 95%;
  }
}
</style>
